import { useState, useMemo } from "react";
import "rsuite/dist/rsuite.css";
import { Helmet } from "react-helmet-async";
import TablePaginationActions from "../../../common/components/pagination.actions";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  TablePagination,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  useTheme,
  TableCell,
  Button,
  Checkbox
} from "@mui/material";
import ProgressLoader from "../../../common/components/progress.loader";
import { useGetAllSurchargeProfileWithPaginationQuery } from "../../../services/API/surchargeApiSlice";
import EnhancedTableHead from "../../../common/components/enhancedtable/enhancedtablehead";
import { Link } from "react-router-dom";
import { SurchargeProfileResponse } from "../../../types/types";

type SortOrder = "asc" | "desc";

const ManageProfiles = () => {
  const theme = useTheme();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [sortField, setSortField] = useState<string>("CreatedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const columns = useMemo(
    () => [
      { label: "Profile Name", accessor: "name", sortable: true },
      { label: "Country", accessor: "countryCode", sortable: true },
      { label: "State/Province", accessor: "stateCode", sortable: true },
      {
        label: "Ask Confirmation",
        accessor: "askConfirmation",
        sortable: false
      },
      { label: "Currency", accessor: "currency", sortable: true },
      { label: "Created On", accessor: "createdAt", sortable: true },
      { label: "Actions", accessor: "", sortable: false }
    ],
    []
  );

  const queryArgs = useMemo(
    () => ({
      page: page + 1,
      pageSize: rowsPerPage,
      sortField,
      sortDirection: sortOrder,
      searchText
    }),
    [page, rowsPerPage, sortField, sortOrder, searchText]
  );

  const { data, isFetching } = useGetAllSurchargeProfileWithPaginationQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: true
    }
  );

  const surchargeProfileList: SurchargeProfileResponse[] = data?.data ?? [];
  const totalCount = data?.rowCount ?? 0;

  // Handlers
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPage(0);
  };

  const handleSorting = (sortField: string, sortOrder: SortOrder) => {
    setSortField(sortField);
    setSortOrder(sortOrder);
  };

  return (
    <>
      <Helmet>
        <title> Surcharge Profiles | {theme.browserTitle} </title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            Surcharge Profiles
          </Typography>

          <Stack direction="row" alignItems="center">
            <TextField
              id="outlined-search"
              placeholder="Search"
              sx={{ width: "300px" }}
              size="small"
              value={searchText}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ ml: 2 }}>
              <Link to="../surchargeSetup">
                <Button variant="contained" color="primary">
                  Setup A New Profile
                </Button>
              </Link>
            </Box>
          </Stack>
        </Stack>

        {/* Table */}
        <Box component={Paper}>
          {isFetching ? (
            <>
              <TableContainer sx={{ height: "80vh" }}>
                <Table size="small" className="sticky-header sortable">
                  <EnhancedTableHead
                    columns={columns}
                    handleSorting={() => {}}
                    currentSortField=""
                    currentSortOrder=""
                  />
                </Table>
              </TableContainer>

              <Box
                sx={{
                  position: "absolute",
                  zIndex: "5",
                  top: "30%",
                  left: "50%"
                }}
              >
                <ProgressLoader isFetching={isFetching}></ProgressLoader>
              </Box>
            </>
          ) : (
            <>
              <TableContainer sx={{ height: "76vh", overflow: "auto" }}>
                <Table size="small" className="sticky-header sortable">
                  <EnhancedTableHead
                    columns={columns}
                    handleSorting={handleSorting}
                    currentSortField={sortField}
                    currentSortOrder={sortOrder}
                  />

                  <TableBody>
                    {surchargeProfileList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>No record found.</TableCell>
                      </TableRow>
                    ) : (
                      surchargeProfileList.map((row: any) => (
                        <TableRow key={row.id}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ width: "170px" }}
                          >
                            {row.name}
                          </TableCell>
                          <TableCell align="left">
                            {row.countryName} ({row.countryCode})
                          </TableCell>
                          <TableCell align="left">
                            {row.stateCode
                              ? row.stateName + "(" + row.stateCode + ")"
                              : "-"}
                          </TableCell>
                          <TableCell align="left">
                            <Checkbox
                              disabled
                              checked={!!row.askConfirmation}
                            />
                          </TableCell>
                          <TableCell align="left">{row.currency}</TableCell>
                          <TableCell align="left">
                            {row.createdAt.toUSDateFormat()}
                          </TableCell>
                          <TableCell>
                            <Link
                              to="../surchargeSetup"
                              state={{ Profile: row }}
                            >
                              Details..
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="end"
                sx={{ mt: 1, borderTop: "1px solid #c8c8c8" }}
              >
                <Table>
                  <TableBody>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: { "aria-label": "rows per page" },
                          native: true
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ManageProfiles;